// API Service for Main Application - supports Google Apps Script or REST
const API_BASE = process.env.REACT_APP_LICENSE_API_BASE || 'https://script.google.com/macros/s/AKfycbzRvDNsyrN4KcA0EWSKVQ_QTQ1b8j_wjiNjg4C0GvH1kjQ27VyBbjrNRhVTSjhKjlOUAg/exec';
const API_KEY = process.env.REACT_APP_LICENSE_API_KEY || 'fiddyscript-license-api-2024-secure-key-xyz789';

// Debug: Log API configuration on load
console.log('🔧 API Configuration:', {
  API_BASE: API_BASE.substring(0, 50) + '...',
  API_KEY_PRESENT: !!API_KEY,
  API_KEY_LENGTH: API_KEY ? API_KEY.length : 0,
  API_KEY_PREVIEW: API_KEY ? API_KEY.substring(0, 10) + '...' : 'MISSING',
  ENV_API_BASE: process.env.REACT_APP_LICENSE_API_BASE ? 'SET' : 'NOT SET',
  ENV_API_KEY: process.env.REACT_APP_LICENSE_API_KEY ? 'SET' : 'NOT SET'
});

class ApiService {
  constructor() {
    this.baseUrl = API_BASE;
  }

  // Generic API call method
  async apiCall(endpoint, options = {}) {
    const isGoogleScript = /script\.googleusercontent|script\.google\.com/.test(this.baseUrl);
    const method = (options.method || 'GET').toUpperCase();
    
    // For Google Apps Script, use POST for PUT/DELETE, but keep GET as GET to avoid CORS preflight
    const httpMethod = isGoogleScript && (method === 'PUT' || method === 'DELETE') ? 'POST' : method;
    
    // Extract query params from options if provided
    const { queryParams } = options;
    const { queryParams: _, ...restOptions } = options;
    
    // Ensure API_KEY is always defined
    const apiKeyToUse = API_KEY || 'fiddyscript-license-api-2024-secure-key-xyz789';
    
    let url = isGoogleScript
      ? `${this.baseUrl}?endpoint=${encodeURIComponent(endpoint)}&method=${encodeURIComponent(method)}&apiKey=${encodeURIComponent(apiKeyToUse)}`
      : `${this.baseUrl}${endpoint}`;
    
    // Add query parameters for Google Apps Script
    if (isGoogleScript && queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        url += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    }
    
    // Debug logging
    if (isGoogleScript) {
      console.log('🌐 apiCall - Google Apps Script request:', {
        endpoint,
        method,
        httpMethod,
        apiKeyPresent: !!apiKeyToUse,
        apiKeyLength: apiKeyToUse ? apiKeyToUse.length : 0,
        urlPreview: url.substring(0, 150) + '...'
      });
    }
    
    // Remove method from options to avoid overriding httpMethod
    const { method: __, ...finalOptions } = restOptions;
    
    let requestOptions = {
      method: httpMethod,
      headers: {
        // Default JSON for non-GAS backends
        ...(finalOptions.body ? { 'Content-Type': 'application/json' } : {}),
        ...finalOptions.headers
      },
      ...finalOptions
    };

    // CORS-friendly settings for Google Apps Script from localhost
    if (isGoogleScript) {
      // Prevent sending cookies; ensure CORS mode
      requestOptions = {
        mode: 'cors',
        credentials: 'omit',
        ...requestOptions
      };
      // Avoid preflight by using text/plain for requests with a body
      const hasBody = !!requestOptions.body;
      if (hasBody) {
        // Replace any custom headers with only text/plain
        requestOptions.headers = { 'Content-Type': 'text/plain;charset=utf-8' };
        // Ensure body is a string
        if (typeof requestOptions.body !== 'string') {
          requestOptions.body = JSON.stringify(requestOptions.body);
        }
      }
    }

    const response = await fetch(url, requestOptions);
    
    // Debug: Log response details
    if (isGoogleScript) {
      console.log('📡 apiCall - Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ apiCall - Error response:', {
        status: response.status,
        errorText: errorText.substring(0, 200)
      });
      throw new Error(`API call failed: ${response.status} - ${errorText}`);
    }
    
    const jsonData = await response.json();
    
    // Debug: Log response data
    if (isGoogleScript) {
      console.log('✅ apiCall - Response data:', {
        success: jsonData.success,
        hasError: !!jsonData.error,
        error: jsonData.error ? jsonData.error.substring(0, 100) : undefined
      });
    }
    
    return jsonData;
  }

  // ===== LICENSE METHODS =====

  // Get license status
  async getLicenseStatus(licenseKey, userEmail = null) {
    const isGoogleScript = /script\.googleusercontent|script\.google\.com/.test(this.baseUrl);
    
    console.log('🔍 getLicenseStatus: Looking up license:', licenseKey);
    console.log('🔍 getLicenseStatus: isGoogleScript:', isGoogleScript);
    
    try {
      // Use apiCall method for consistency
      const result = await this.apiCall(`/api/licenses/lookup/${licenseKey}`, {
        method: 'GET'
      });
      
      if (result.success && result.found && result.license) {
        return {
          found: true,
          license: result.license
        };
      } else {
        return {
          found: false,
          license: null
        };
      }
    } catch (error) {
      console.error('❌ Error getting license status:', error);
      return {
        found: false,
        license: null
      };
    }
  }

  // Register system with license
  async registerSystem(licenseKey, systemId, systemFingerprint, userEmail = '', deviceName = '', platform = '') {
    return this.apiCall('/api/licenses/register', {
      method: 'POST',
      body: JSON.stringify({
        licenseKey,
        systemId,
        systemFingerprint,
        userEmail,
        deviceName,
        platform
      })
    });
  }

  // ===== TRIAL METHODS =====

  // Check if system can use trial
  async checkTrialEligibility(systemId) {
    const isGoogleScript = /script\.googleusercontent|script\.google\.com/.test(this.baseUrl);
    
    console.log('🔍 checkTrialEligibility: Calling API with systemId:', systemId);
    console.log('🔍 checkTrialEligibility: isGoogleScript:', isGoogleScript);
    
    // Use apiCall method for consistency
    // For Google Apps Script, pass systemId as query parameter
    const endpoint = isGoogleScript 
      ? `/api/licenses/trial/check`
      : `/api/licenses/trial/check/${systemId}`;
    
    const result = await this.apiCall(endpoint, {
      method: 'GET',
      queryParams: isGoogleScript ? { systemId } : undefined
    });
    
    console.log('✅ checkTrialEligibility: API result:', result);
    return result;
  }

  // Activate trial
  async activateTrial(trialDurationDays = 7) {
    const systemId = this.getStoredSystemId() || this.generateSystemId();
    const systemFingerprint = this.getSystemFingerprint();
    
    try {
      console.log('Activating trial with:', {
        systemId,
        systemFingerprint,
        durationDays: trialDurationDays,
        userAgent: navigator.userAgent
      });
      
      const response = await this.apiCall('/api/licenses/trial/activate', {
        method: 'POST',
        body: JSON.stringify({
          systemId,
          systemFingerprint,
          durationDays: trialDurationDays,
          userAgent: navigator.userAgent,
          userEmail: ''
        })
      });
      
      console.log('Trial activation response:', response);
      
      if (response.success) {
        this.storeSystemId(systemId);
        return response.trial;
      } else {
        throw new Error(response.error || 'Failed to activate trial');
      }
    } catch (error) {
      console.error('Trial activation error:', error);
      throw error;
    }
  }

  // Get trial status
  async getTrialStatus(systemId) {
    const isGoogleScript = /script\.googleusercontent|script\.google\.com/.test(this.baseUrl);
    
    console.log('🔍 getTrialStatus called with systemId:', systemId);
    console.log('🔍 isGoogleScript:', isGoogleScript);
    console.log('🔍 baseUrl:', this.baseUrl);
    
    // Use apiCall method for consistency
    // For Google Apps Script, pass systemId as query parameter
    const endpoint = isGoogleScript 
      ? `/api/licenses/trial/status`
      : `/api/licenses/trial/status/${systemId}`;
    
    return this.apiCall(endpoint, {
      method: 'GET',
      queryParams: isGoogleScript ? { systemId } : undefined
    });
  }

  // Increment trial usage
  async incrementTrialUsage(systemId, feature) {
    return this.apiCall(`/api/licenses/trial/usage/${systemId}`, {
      method: 'POST',
      body: JSON.stringify({ feature })
    });
  }

  // ===== SYSTEM ID GENERATION =====

  // Generate system ID based on browser fingerprint (unified algorithm)
  generateSystemId() {
    // Get browser fingerprint components
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Hello, world!', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Hello, world!', 4, 17);
    const canvasFingerprint = canvas.toDataURL();

    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const platform = navigator.platform;
    const hardwareConcurrency = navigator.hardwareConcurrency;
    const timezoneOffset = new Date().getTimezoneOffset();
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const vendor = navigator.vendor;

    // Combine components into a hash
    const fingerprint = `${userAgent}-${language}-${platform}-${hardwareConcurrency}-${timezoneOffset}-${screenResolution}-${vendor}-${canvasFingerprint}`;
    const hash = this.hashCode(fingerprint);
    
    // Prepend with 'web_' to indicate it's a web-generated ID
    return `web_${hash}`;
  }

  // Simple hash function
  hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36); // Convert to base36 string
  }

  // Get system fingerprint
  getSystemFingerprint() {
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.platform,
      navigator.cookieEnabled,
      typeof(Storage) !== 'undefined'
    ];
    
    return btoa(components.join('|')).substring(0, 50);
  }

  // ===== STORAGE METHODS =====

  // Store license key
  storeLicenseKey(licenseKey) {
    localStorage.setItem('licenseKey', licenseKey);
  }

  // Get stored license key
  getStoredLicenseKey() {
    return localStorage.getItem('licenseKey');
  }

  // Clear stored license
  clearStoredLicense() {
    localStorage.removeItem('licenseKey');
  }

  // Clear stored system ID
  clearStoredSystemId() {
    localStorage.removeItem('systemId');
  }

  // Store system ID
  storeSystemId(systemId) {
    localStorage.setItem('systemId', systemId);
  }

  // Get stored system ID
  getStoredSystemId() {
    return localStorage.getItem('systemId');
  }

  // ===== VALIDATION METHODS =====

  // Quick license check (for app initialization)
  async quickLicenseCheck() {
    const storedLicenseKey = this.getStoredLicenseKey();
    const systemId = this.getStoredSystemId() || this.generateSystemId();
    
    if (!systemId) {
      const newSystemId = this.generateSystemId();
      this.storeSystemId(newSystemId);
    }

    // Check for stored license key first
    if (storedLicenseKey) {
      try {
        const licenseStatus = await this.getLicenseStatus(storedLicenseKey);
        if (licenseStatus.found && licenseStatus.license.isActive) {
          return { 
            success: true, 
            isValid: true, 
            needsValidation: false, 
            reason: 'License key found and active.',
            isTrial: false,
            license: licenseStatus.license
          };
        }
      } catch (error) {
        console.error('License validation error:', error);
      }
    }

    // Check for active trial - this is important after trial activation
    try {
      const trialStatus = await this.getTrialStatus(systemId);
      if (trialStatus.found && trialStatus.trial) {
        const trial = trialStatus.trial;
        // Handle both camelCase and snake_case from API
        const isActive = trial.isActive !== false && trial.is_active !== false;
        const isExpired = trial.isExpired === true || trial.is_expired === true;
        const expiryDate = trial.expiryDate || trial.expiry_date || trial.expires_at || trial.expiresAt;
        
        // Check expiration by date as well
        let isActuallyExpired = isExpired;
        if (expiryDate) {
          const exp = new Date(expiryDate);
          const now = new Date();
          if (!isNaN(exp.getTime()) && now >= exp) {
            isActuallyExpired = true;
          }
        }
        
        // Only return valid trial if it's active AND not expired
        if (isActive && !isActuallyExpired) {
          console.log('✅ Active trial found:', trial);
          return { 
            success: true, 
            isValid: true, 
            needsValidation: false, 
            reason: 'Trial active.',
            isTrial: true,
            trial: trial
          };
        } else {
          console.log('⚠️ Trial found but expired or inactive:', { isActive, isExpired: isActuallyExpired });
        }
      }
    } catch (trialError) {
      console.log('Trial status check failed:', trialError);
    }

  // Check for trial availability (only if no active trial found)
  try {
    const canUseTrial = await this.canUseTrial();
    if (canUseTrial) {
      return { 
        success: true, 
        isValid: false, 
        needsValidation: true, 
        reason: 'No active license found, but trial is available.',
        isTrial: true
      };
    }
  } catch (trialError) {
    console.log('Trial availability check failed:', trialError);
  }

  return { 
    success: true, 
    isValid: false, 
    needsValidation: true, 
    reason: 'No active license or trial found.' 
  };
  }

  // Validate license key
  async validateLicense(licenseKey) {
    try {
      const systemId = this.getStoredSystemId() || this.generateSystemId();
      const systemFingerprint = this.getSystemFingerprint();
      
      // Check if the entered "license key" is actually a System ID (trial license)
      // Trial System IDs often start with "TEST_" or contain patterns that indicate they're system IDs
      const looksLikeSystemId = licenseKey.includes('_') || licenseKey.startsWith('TEST_') || licenseKey.length > 20;
      
      console.log('🔍 validateLicense - licenseKey:', licenseKey);
      console.log('🔍 validateLicense - looksLikeSystemId:', looksLikeSystemId);
      
      if (looksLikeSystemId) {
        // This might be a System ID for a trial - check trial status
        console.log('✅ Detected System ID pattern, checking trial status...');
        try {
          const trialStatus = await this.getTrialStatus(licenseKey);
          console.log('✅ Trial status received:', JSON.stringify(trialStatus, null, 2));
          console.log('🔍 Checking trialStatus.found:', trialStatus.found);
          console.log('🔍 Checking trialStatus.trial:', trialStatus.trial);
          console.log('🔍 Checking trialStatus.success:', trialStatus.success);
          
          // Handle Google Apps Script response format
          if (trialStatus.success !== false && trialStatus.found && trialStatus.trial) {
            const now = new Date();
            const expiryDate = new Date(trialStatus.trial.expiryDate);
            const isActive = trialStatus.trial.isActive && (now <= expiryDate);
            
            console.log('📊 Trial validation details:', {
              isActive: trialStatus.trial.isActive,
              expiryDate: trialStatus.trial.expiryDate,
              now: now.toISOString(),
              expiryDateObj: expiryDate.toISOString(),
              expired: now > expiryDate,
              willBeActive: isActive
            });
            
            if (isActive) {
              // Valid active trial - store the system ID and allow access
              this.storeSystemId(licenseKey);
              
              console.log('✅ Trial license validated successfully!');
              return {
                success: true,
                isValid: true,
                needsValidation: false,
                reason: 'Trial license validated successfully.',
                isTrial: true,
                license: {
                  licenseKey: licenseKey,
                  isActive: true,
                  isTrial: true,
                  expiryDate: trialStatus.trial.expiryDate
                }
              };
            } else {
              console.log('❌ Trial is inactive or expired');
              return {
                success: false,
                isValid: false,
                needsValidation: true,
                reason: trialStatus.trial.isActive ? 'Trial license has expired.' : 'Trial license is inactive.'
              };
            }
          } else {
            console.log('❌ Trial not found or response format unexpected');
            console.log('❌ Response structure:', {
              success: trialStatus.success,
              found: trialStatus.found,
              hasTrial: !!trialStatus.trial,
              fullResponse: trialStatus
            });
          }
        } catch (trialError) {
          // If trial check fails, continue to regular license check
          console.error('❌ Trial check failed with error:', trialError);
          console.error('❌ Error details:', {
            message: trialError.message,
            stack: trialError.stack
          });
        }
      } else {
        console.log('ℹ️ Does not look like System ID, checking regular license...');
      }
      
      // First check if license exists and is valid
      console.log('🔍 Checking regular license status for:', licenseKey);
      const licenseStatus = await this.getLicenseStatus(licenseKey);
      console.log('✅ License status response:', JSON.stringify(licenseStatus, null, 2));
      
      if (!licenseStatus.found) {
        console.log('❌ License not found');
        return { 
          success: false, 
          isValid: false, 
          needsValidation: true, 
          reason: 'License not found.' 
        };
      }
      
      if (!licenseStatus.license) {
        console.log('❌ License data missing');
        return { 
          success: false, 
          isValid: false, 
          needsValidation: true, 
          reason: 'License data not available.' 
        };
      }
      
      console.log('🔍 License details:', {
        licenseKey: licenseStatus.license.licenseKey,
        isActive: licenseStatus.license.isActive,
        isLifetime: licenseStatus.license.isLifetime,
        expiryDate: licenseStatus.license.expiryDate
      });
      
      if (!licenseStatus.license.isActive) {
        console.log('❌ License is inactive');
        return { 
          success: false, 
          isValid: false, 
          needsValidation: true, 
          reason: 'License is inactive.' 
        };
      }

      // Check if license is expired
      if (!licenseStatus.license.isLifetime && licenseStatus.license.expiryDate) {
        const expiryDate = new Date(licenseStatus.license.expiryDate);
        const now = new Date();
        console.log('🔍 Checking expiry:', {
          expiryDate: expiryDate.toISOString(),
          now: now.toISOString(),
          expired: now > expiryDate
        });
        
        if (now > expiryDate) {
          console.log('❌ License has expired');
          return { 
            success: false, 
            isValid: false, 
            needsValidation: true, 
            reason: 'License has expired.' 
          };
        }
      }

      // Register system with license
      try {
        await this.registerSystem(licenseKey, systemId, systemFingerprint, '', '', 'web');
        this.storeLicenseKey(licenseKey);
        this.storeSystemId(systemId);
        
        return { 
          success: true, 
          isValid: true, 
          needsValidation: false, 
          reason: 'License validated and system registered successfully.' 
        };
      } catch (regError) {
        // If registration fails but license is valid, still allow access
        console.warn('System registration failed:', regError);
        this.storeLicenseKey(licenseKey);
        this.storeSystemId(systemId);
        
        return { 
          success: true, 
          isValid: true, 
          needsValidation: false, 
          reason: 'License validated successfully.' 
        };
      }
    } catch (error) {
      console.error('License validation error:', error);
      return { 
        success: false, 
        isValid: false, 
        needsValidation: true, 
        error: error.message 
      };
    }
  }

  // Check if system can use trial
  async canUseTrial() {
    const systemId = this.getStoredSystemId() || this.generateSystemId();
    console.log('canUseTrial: Checking with systemId:', systemId);
    try {
      const response = await this.checkTrialEligibility(systemId);
      console.log('canUseTrial: API response:', response);
      console.log('canUseTrial: response.canUseTrial:', response.canUseTrial);
      console.log('canUseTrial: response.canActivate:', response.canActivate);
      console.log('canUseTrial: response keys:', Object.keys(response));
      
      // Use the correct field name from API response
      return response.canActivate || false;
    } catch (error) {
      console.error('Trial eligibility check error:', error);
      return false;
    }
  }

  // Activate trial
  async activateTrial(trialDurationDays = 7) {
    const systemId = this.getStoredSystemId() || this.generateSystemId();
    const systemFingerprint = this.getSystemFingerprint();
    
    try {
      console.log('Activating trial with:', {
        systemId,
        systemFingerprint,
        durationDays: trialDurationDays,
        userAgent: navigator.userAgent
      });
      
      const response = await this.apiCall('/api/licenses/trial/activate', {
        method: 'POST',
        body: JSON.stringify({
          systemId,
          systemFingerprint,
          durationDays: trialDurationDays, // Fixed: API expects 'durationDays'
          userAgent: navigator.userAgent,
          userEmail: ''
        })
      });
      
      console.log('Trial activation response:', response);
      
      if (response.success) {
        this.storeSystemId(systemId);
        return response.trial;
      } else {
        throw new Error(response.error || 'Failed to activate trial');
      }
    } catch (error) {
      console.error('Trial activation error:', error);
      throw error;
    }
  }

  // Get trial info
  async getTrialInfo() {
    const systemId = this.getStoredSystemId();
    if (!systemId) return null;
    
    try {
      const response = await this.getTrialStatus(systemId);
      if (response.found) {
        return response.trial;
      }
      return null;
    } catch (error) {
      console.error('Error getting trial info:', error);
      return null;
    }
  }

  // Check feature access
  async checkFeatureAccess(feature) {
    const storedLicenseKey = this.getStoredLicenseKey();
    
    // Licensed users have full access
    if (storedLicenseKey) {
      return { hasAccess: true, reason: 'Licensed user.' };
    }

    // Check trial access
    const systemId = this.getStoredSystemId();
    if (!systemId) {
      return { hasAccess: false, reason: 'No system ID found.' };
    }

    try {
      const trialInfo = await this.getTrialInfo();
      if (!trialInfo || !trialInfo.is_active || trialInfo.is_expired) {
        return { hasAccess: false, reason: 'No active trial.' };
      }

      // Check usage limits
      const limits = {
        humanize: 100,
        script: 50
      };
      
      const currentUsage = feature === 'humanize' ? 
        trialInfo.usage_stats.humanizations : 
        trialInfo.usage_stats.scripts;
        
      const maxUsage = limits[feature] || 0;
      
      if (currentUsage >= maxUsage) {
        return { 
          hasAccess: false, 
          reason: `Usage limit reached for ${feature} (${currentUsage}/${maxUsage})` 
        };
      }

      return { hasAccess: true, reason: 'Trial active.' };
    } catch (error) {
      console.error('Error checking feature access:', error);
      return { hasAccess: false, reason: 'Error checking access.' };
    }
  }

  // Increment usage
  async incrementUsage(feature) {
    const storedLicenseKey = this.getStoredLicenseKey();
    
    // Licensed users have unlimited usage
    if (storedLicenseKey) {
      return true;
    }

    // Increment trial usage
    const systemId = this.getStoredSystemId();
    if (!systemId) {
      return false;
    }

    try {
      const response = await this.incrementTrialUsage(systemId, feature);
      return response.success;
    } catch (error) {
      console.error('Error incrementing usage:', error);
      return false;
    }
  }

  // Clear all stored data
  clearAllData() {
    this.clearStoredLicense();
    this.clearStoredSystemId();
  }
}

const apiService = new ApiService();
export default apiService;
